import { combineReducers, compose as _compose, UnknownAction } from 'redux'
import { ThunkAction, configureStore } from '@reduxjs/toolkit'
import { logger } from 'redux-logger'
import undoable, { excludeAction, groupByActionTypes } from 'redux-undo'
import throttle from 'lodash-es/throttle'

import client from '~/client'

import auth from './auth'
import alert from './alert'
import camera, {
  zoomIn,
  zoomOut,
  setZoom,
  set3D,
  set2D,
  set3DRotation,
  set3DPosition,
  set2DPosition,
  set2DZoom,
  start2DFit,
  fitFinished,
} from './camera'
import layers, { TOGGLE_LAYER_VISIBILITY } from './layers'
import objects, {
  ADD_CEILING,
  UPDATE_CEILING,
  DELETE_CEILING,
  UPDATE_ROOF,
  UPDATE_ROOF_SECTION,
  REQUEST_AIRFLOW,
  RECEIVE_AIRFLOW,
  SET_AIRFLOW_LAYER,
  LOAD_FACILITY,
  RESET_FACILITY,
  ADD_DIMENSION,
  UPDATE_OBJECTS_PASSIVELY,
  REQUEST_HEAT_MAP,
  RECEIVE_HEAT_MAP,
  UPDATE_HEAT_MAP_STATUS,
  SET_HEAT_MAP_LAYER,
  UPDATE_OBJECTS,
} from './objects'
import objectsPersistence, { SAVE_OBJECTS_STARTED, SAVE_OBJECTS_SUCCEEDED } from './objectsPersistence'
import panel from './panel'
import selectedObjects, { DELETE_SELECTED_OBJECTS, SELECT_OBJECTS } from './selectedObjects'
import tools from './tools'
import cfd from './cfd'
import loadingDock from './loadingDock'
import userInterface from './userInterface'
import quote from './quote'
import backgroundImageForm from './backgroundImageForm'
import status, { CLEAR_STATUS, SET_STATUS } from './status'
import units from './units'
import router from './router'
import install from './install'

const reducers = combineReducers({
  auth,
  alert,
  camera,
  layers,
  objects: undoable(objects, {
    debug: !import.meta.env.PROD && import.meta.env.MODE !== 'test',
    syncFilter: true,
    groupBy: groupByActionTypes([ADD_DIMENSION, UPDATE_OBJECTS]),
    filter: excludeAction([
      LOAD_FACILITY,
      RESET_FACILITY,
      UPDATE_ROOF,
      UPDATE_ROOF_SECTION,
      REQUEST_AIRFLOW,
      RECEIVE_AIRFLOW,
      SET_AIRFLOW_LAYER,
      TOGGLE_LAYER_VISIBILITY,
      ADD_CEILING,
      UPDATE_CEILING,
      DELETE_CEILING,
      UPDATE_OBJECTS_PASSIVELY,
      REQUEST_HEAT_MAP,
      RECEIVE_HEAT_MAP,
      UPDATE_HEAT_MAP_STATUS,
      SET_HEAT_MAP_LAYER,
      SAVE_OBJECTS_STARTED,
      SAVE_OBJECTS_SUCCEEDED,
      SELECT_OBJECTS,
      SET_STATUS,
      CLEAR_STATUS,
      DELETE_SELECTED_OBJECTS,
      zoomIn.type,
      zoomOut.type,
      setZoom.type,
      set3D.type,
      set2D.type,
      set3DRotation.type,
      set3DPosition.type,
      set2DPosition.type,
      set2DZoom.type,
      start2DFit.type,
      fitFinished.type,
    ]),
  }),
  objectsPersistence,
  panel,
  selectedObjects,
  tools,
  cfd,
  loadingDock,
  userInterface,
  quote,
  backgroundImageForm,
  status,
  units,
  router,
  install,
})

const AUTH_STORE_LS_KEY = 'BAF:UI:store'

const loadState = () => {
  try {
    const serializedState = localStorage.getItem(AUTH_STORE_LS_KEY)
    if (serializedState === null) {
      return undefined
    }
    return JSON.parse(serializedState)
  } catch {
    return undefined
  }
}

const saveState = (state: any) => {
  try {
    const serializedState = JSON.stringify(state)
    localStorage.setItem(AUTH_STORE_LS_KEY, serializedState)
  } catch {
    // Ignore write errors.
  }
}

const persistedState = loadState()

export function setupStore(preloadedState?: Partial<ReturnType<typeof reducers>>) {
  return configureStore({
    reducer: reducers,
    preloadedState,
    devTools: import.meta.env.DEV,
    middleware: getDefaultMiddleware => {
      const middlewares = getDefaultMiddleware({
        thunk: {
          extraArgument: client,
        },
        serializableCheck: false,
        immutableCheck: false,
      })
      if ('__REDUX_DEVTOOLS_EXTENSION__' in window) {
        return middlewares
      }
      if (import.meta.env.MODE !== 'development') {
        return middlewares
      }
      return middlewares.concat(logger)
    },
  })
}

const store = setupStore(persistedState)

export type RootState = ReturnType<typeof store.getState>
export type AppStore = typeof store
export type AppDispatch = AppStore['dispatch']
export type AppGetState = AppStore['getState']
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, UnknownAction>

store.subscribe(
  throttle(() => {
    saveState({
      auth: store.getState().auth,
    })
  }, 1000)
)

export default store
